<template>
    <div class="bg-white dark:bg-slate-800 rounded-lg shadow-md border border-slate-200 dark:border-slate-700">
        <div class="overflow-x-auto">
            <div class="min-w-max">
                <div
                    class="flex rounded-t-lg border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 sticky top-0 z-10"
                >
                    <div
                        v-for="column in columns"
                        :key="column.key"
                        :class="['px-6 py-3', column.width]"
                    >
                        <span
                            class="text-slate-500 dark:text-slate-400 text-sm font-medium whitespace-nowrap font-['Inter'] leading-tight"
                        >
                            {{ column.label }}
                        </span>
                    </div>
                    <div class="px-6 py-3 w-32 shrink-0"></div>
                </div>

                <div class="relative">
                    <template v-if="!isGrouped && !loading && benefits.length">
                        <div
                            v-for="row in benefits"
                            :key="`${row.id}-${row.connected_account_id}`"
                            class="flex items-center hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors border-b border-slate-200 dark:border-slate-700 py-4"
                            :class="
                                locked ? 'cursor-default' : 'cursor-pointer'
                            "
                            @click="!locked && openDetails(row.item)"
                        >
                            <BenefitRow
                                :row="row"
                                :columns="columns"
                                :show-card-numbers="insightsShowCardNumbers"
                                :cdn-url="cdnUrl"
                                :show-card-info="true"
                                :locked="locked"
                                @open="openDetails"
                            />
                        </div>
                    </template>

                    <!-- Grouped By Card -->
                    <template
                        v-else-if="isGrouped && !loading && benefits.length"
                    >
                        <template
                            v-for="(group, gIdx) in groups"
                            :key="group.key"
                        >
                            <!-- Card header row -->
                            <div
                                class="flex items-center hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors border-slate-200 dark:border-slate-700 py-4 cursor-pointer"
                                :class="{ 'border-t': gIdx !== 0 }"
                                @click="toggleGroup(group.key)"
                            >
                                <!-- Benefit cell: card image + card name + Up to pill -->
                                <div
                                    class="flex items-center min-w-0 px-6 gap-3"
                                    :class="colWidth('benefit')"
                                >
                                    <div class="shrink-0">
                                        <ImageLoader>
                                            <template #image>
                                                <img
                                                    v-if="
                                                        group.card_image
                                                            ?.path &&
                                                        group.card_image
                                                            ?.file_name
                                                    "
                                                    :src="`${cdnUrl}/${group.card_image.path}/_small/${group.card_image.file_name}`"
                                                    :alt="group.card_name"
                                                    class="w-12 h-8 rounded-sm"
                                                />
                                            </template>
                                            <template #error>
                                                <div
                                                    class="w-12 h-8 bg-slate-100 dark:bg-slate-700 rounded-sm border border-slate-200 dark:border-slate-700 flex items-center justify-center"
                                                >
                                                    <Icon
                                                        icon="credit-card"
                                                        class="w-4 h-4 text-slate-400"
                                                    />
                                                </div>
                                            </template>
                                        </ImageLoader>
                                    </div>
                                    <div class="min-w-0 flex-1">
                                        <div
                                            class="font-medium text-slate-800 dark:text-slate-100 text-sm flex items-center gap-1"
                                        >
                                            <span class="block truncate">
                                                {{ group.card_name }}
                                            </span>
                                            <span
                                                v-if="
                                                    insightsShowCardNumbers &&
                                                    group.card_last_4
                                                "
                                                class="text-slate-500 dark:text-slate-400 text-xs shrink-0"
                                            >
                                                ({{ group.card_last_4 }})
                                            </span>
                                        </div>
                                        <div
                                            v-if="group.annual_total > 0"
                                            class="text-slate-500 dark:text-slate-400 text-sm py-1 px-2 bg-slate-100 dark:bg-slate-700 rounded-lg max-w-fit font-medium mt-1"
                                        >
                                            Up to
                                            {{
                                                formatMoney(group.annual_total)
                                            }}/year
                                        </div>
                                    </div>
                                </div>

                                <!-- User: empty for header (only when shown) -->
                                <div
                                    v-if="showUserColumn"
                                    class="px-6 flex items-center"
                                    :class="colWidth('user')"
                                ></div>

                                <!-- Value up to: empty for header -->
                                <div
                                    class="px-6 flex items-center"
                                    :class="colWidth('value_up_to')"
                                ></div>

                                <!-- Used selected period: aggregate -->
                                <div
                                    class="px-6 gap-2 flex flex-col"
                                    :class="colWidth('used')"
                                >
                                    <button
                                        v-if="locked"
                                        type="button"
                                        class="flex items-center gap-1 text-orange text-sm font-medium hover:underline cursor-pointer bg-transparent border-none p-0"
                                        @click.stop="openUpgrade"
                                    >
                                        <Icon icon="lock" class="w-4 h-4" />
                                        <span>Upgrade</span>
                                    </button>
                                    <template v-else>
                                        <div
                                            class="w-full max-w-[120px] bg-orange-200 rounded-full h-1 relative"
                                        >
                                            <div
                                                class="bg-10x-orange h-1 rounded-full transition-all"
                                                :style="{
                                                    width:
                                                        clamp(
                                                            group.utilization
                                                        ) + '%',
                                                }"
                                            ></div>
                                            <div
                                                v-if="
                                                    Number(group.utilization) >=
                                                    100
                                                "
                                                class="absolute -right-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-10x-orange rounded-full flex items-center justify-center border-2 border-white"
                                            >
                                                <Icon
                                                    icon="check"
                                                    svgClass="w-2 h-2 text-white"
                                                    class="text-white"
                                                />
                                            </div>
                                        </div>
                                        <div
                                            class="text-sm text-slate-500 dark:text-slate-400 font-normal whitespace-nowrap"
                                        >
                                            {{ formatMoney(group.used) }} of
                                            {{ formatMoney(group.total) }}
                                            <span class="text-xs">
                                                (used YTD)
                                            </span>
                                        </div>
                                    </template>
                                </div>

                                <!-- Unused: empty for header -->
                                <div
                                    class="px-6 flex items-center"
                                    :class="colWidth('unused')"
                                ></div>

                                <!-- Utilization: aggregate -->
                                <div
                                    class="px-6 flex items-center"
                                    :class="colWidth('utilization')"
                                >
                                    <span
                                        v-if="locked"
                                        class="text-slate-400 text-sm"
                                    >
                                        —
                                    </span>
                                    <span
                                        v-else
                                        class="text-slate-800 dark:text-slate-100 text-sm font-medium"
                                    >
                                        {{ group.utilization }}%
                                    </span>
                                </div>

                                <!-- Updates: empty for header -->
                                <div
                                    class="px-6 flex items-center"
                                    :class="[
                                        colWidth('frequency'),
                                        locked && 'opacity-50',
                                    ]"
                                ></div>

                                <!-- Action: expand/collapse chevron -->
                                <div class="w-32 shrink-0 flex justify-end">
                                    <div class="flex items-center px-6">
                                        <button
                                            type="button"
                                            class="btn-icon !rounded-full"
                                            :aria-label="
                                                isExpanded(group.key)
                                                    ? 'Collapse card group'
                                                    : 'Expand card group'
                                            "
                                            @click.stop="toggleGroup(group.key)"
                                        >
                                            <Icon
                                                icon="chevron-down"
                                                svgClass="m-1"
                                                class="w-3 h-3 transition-transform duration-200"
                                                :class="{
                                                    'rotate-180': isExpanded(
                                                        group.key
                                                    ),
                                                }"
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Expanded benefit rows under this card -->
                            <template v-if="isExpanded(group.key)">
                                <div
                                    v-for="(row, rowIdx) in group.rows"
                                    :key="`${group.key}-${row.id}-${row.connected_account_id}`"
                                    class="flex items-center hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors border-t border-slate-200 dark:border-slate-700 py-4 relative group ml-12"
                                    :class="
                                        locked
                                            ? 'cursor-default'
                                            : 'cursor-pointer'
                                    "
                                    @click="!locked && openDetails(row.item)"
                                >
                                    <BenefitRow
                                        :row="row"
                                        :columns="columns"
                                        :show-card-numbers="
                                            insightsShowCardNumbers
                                        "
                                        :cdn-url="cdnUrl"
                                        :show-card-info="false"
                                        :indent="true"
                                        :index="rowIdx"
                                        :locked="locked"
                                        @open="openDetails"
                                    />
                                </div>
                            </template>
                        </template>
                    </template>

                    <div
                        v-if="loading || !benefits.length"
                        class="px-6 py-16 flex items-center justify-center min-h-[240px]"
                    >
                        <ChartStatePlaceholder :loading="loading" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Icon from '@/components/ui/icon/icon.vue';
import BenefitRow from './insights-table-row.vue';
import ChartStatePlaceholder from './chart-state-placeholder.vue';

const props = defineProps({
    benefits: {
        type: Array,
        required: true,
    },
});

const store = useCardBenefitsStore();
const {
    insightsGroupBy,
    insightsShowCardNumbers,
    insightsBenefitsGrouped,
    loading,
    isSubscribed,
} = storeToRefs(store);
const cdnUrl = computed(() => store.cdnUrl);

// Free users see the table structure but the historical aggregates,
// progress bars, and detail navigation are gated behind 10xTravel+.
const locked = computed(() => !isSubscribed.value);
const openUpgrade = () => store.setShowUpgradeModal(true);

const showUserColumn = computed(() => store.insightsUserOptions.length > 1);

// Column set adapts to wallet shape: when there are multiple users, an extra
// `User` column is inserted between `Benefit` and `Value up to`.
const columns = computed(() => {
    const base = [
        { key: 'benefit', label: 'Benefit', width: 'w-96 grow' },
        { key: 'value_up_to', label: 'Value up to', width: 'w-32 grow' },
        { key: 'used', label: 'Used selected period', width: 'w-64 grow' },
        { key: 'unused', label: 'Unused', width: 'w-32 grow' },
        { key: 'utilization', label: 'Utilization', width: 'w-32 grow' },
        { key: 'frequency', label: 'Updates', width: 'w-32 grow' },
    ];
    if (showUserColumn.value) {
        base.splice(1, 0, {
            key: 'user',
            label: 'User',
            width: 'w-32 grow',
        });
    }
    return base;
});

const colWidth = (key) => {
    const col = columns.value.find((c) => c.key === key);
    return col ? col.width : '';
};

const isGrouped = computed(() => insightsGroupBy.value === 'by_card');
const groups = computed(() => insightsBenefitsGrouped.value.groups || []);

// All groups collapsed by default; prune stale keys when groups change.
const expanded = ref(new Set());
watch(groups, (newGroups) => {
    const validKeys = new Set(newGroups.map((g) => g.key));
    const next = new Set();
    expanded.value.forEach((k) => {
        if (validKeys.has(k)) next.add(k);
    });
    expanded.value = next;
});

const isExpanded = (key) => expanded.value.has(key);

const toggleGroup = (key) => {
    const next = new Set(expanded.value);
    if (next.has(key)) {
        next.delete(key);
    } else {
        next.add(key);
    }
    expanded.value = next;
};

const clamp = (n) => Math.max(0, Math.min(100, Number(n) || 0));

const formatMoney = (value) => {
    if (!value && value !== 0) return '$0';
    return '$' + Math.round(Number(value)).toLocaleString('en-US');
};

const openDetails = (item) => {
    if (!item) return;
    store.openBenefitDetails(item);
};
</script>
